import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  RegisterRequest,
  type RegisterRequest as RegisterRequestType,
} from "@vedaunion/shared";
import { EditorialAuthShell } from "../components/auth/EditorialAuthShell";
import { AuthInput } from "../components/auth/AuthInput";
import { useMe, useRegister } from "../lib/auth";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const me = useMe();
  const { mutateAsync, isPending } = useRegister();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterRequestType>({
    resolver: zodResolver(RegisterRequest),
    defaultValues: { email: "", password: "", displayName: "" },
  });

  useEffect(() => {
    if (me.data?.user) {
      const next = search.get("next") || "/dashboard";
      navigate(next, { replace: true });
    }
  }, [me.data?.user, navigate, search]);

  const onSubmit = async (values: RegisterRequestType) => {
    // Drop empty optional fields so the schema doesn't validate ""
    const body: RegisterRequestType = {
      email: values.email,
      password: values.password,
      displayName: values.displayName,
      ...(values.dateOfBirth ? { dateOfBirth: values.dateOfBirth } : {}),
    };
    try {
      await mutateAsync(body);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const e = err as { status?: number; body?: { error?: string; message?: string }; message?: string };
      const message = e?.body?.message ?? e?.message ?? "Registration failed.";
      if (e?.body?.error === "email-taken") {
        setError("email", { type: "server", message });
      } else if (e?.body?.error === "invalid-password") {
        setError("password", { type: "server", message });
      } else {
        setError("root", { type: "server", message });
      }
    }
  };

  return (
    <EditorialAuthShell
      eyebrow="Begin"
      headline={
        <>
          A name in the{" "}
          <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
            manuscript
          </span>
          .
        </>
      }
      lede="Free to join. You can fill in the rest later in your profile."
      verse={{
        deva: "विद्यायाऽमृतमश्नुते",
        iast: "Vidyayā amṛtam aśnute",
        english: "Through knowledge, one attains the immortal.",
        source: "Īśa Upaniṣad 11",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <AuthInput
          label="Display name"
          autoComplete="name"
          autoFocus
          {...register("displayName")}
          error={errors.displayName?.message}
        />
        <AuthInput
          label="Email"
          type="email"
          autoComplete="email"
          {...register("email")}
          error={errors.email?.message}
        />
        <AuthInput
          label="Password"
          type="password"
          autoComplete="new-password"
          {...register("password")}
          error={errors.password?.message}
          hint="At least 12 characters."
        />

        <div className="auth-actions">
          <button type="submit" className="btn-primary auth-submit" disabled={isPending}>
            <span>{isPending ? "Creating account…" : "Begin"}</span>
            <span className="btn-arrow-icon" aria-hidden>
              →
            </span>
          </button>
        </div>

        {errors.root && <p className="auth-error mt-3">— {errors.root.message}</p>}

        <div className="auth-links">
          <Link to="/login">Already have an account? Sign in →</Link>
        </div>
      </form>
    </EditorialAuthShell>
  );
}
